import { useCallback, useRef } from 'react';

/**
 * Custom hook that provides drag-and-drop functionality for a list of items.
 *
 * @param {Function} onChange - Callback function that is triggered when the order of items changes.
 * @param {Array} pickerItems - Array of items that can be dragged and dropped.
 * @param {Function} setPickerItems - Callback function to update the list of items.
 *
 * @returns {Object} An object containing drag-and-drop event handlers:
 *  - handleDragStart: Starts the drag operation for an item.
 *  - handleDragEnd: Cleans up after the drag operation is complete.
 *  - handleDragOver: Allows an item to be dragged over a drop target.
 *  - handleDrop: Handles the drop event, updates item order, and triggers the onChange callback.
 */

const useDragDrop = ({
  onChange = () => {},
  pickerItems = [],
  setPickerItems = () => {},
}) => {
  const draggingItem = useRef(null);

  const handleDragStart = useCallback((e, item) => {
    draggingItem.current = item;
    e.dataTransfer.setData('text/plain', '');
  }, []);

  const handleDragEnd = useCallback(() => {
    draggingItem.current = null;
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e, targetItem) => {
      if (!draggingItem.current) return;

      const currentIndex = pickerItems.indexOf(draggingItem.current);
      const targetIndex = pickerItems.indexOf(targetItem);

      const lastCheckedIndex = pickerItems.reduce(
        (lastIndex, currentItem, idx) =>
          currentItem.isChecked ? idx : lastIndex,
        -1
      );

      if (targetIndex > lastCheckedIndex) return;

      if (currentIndex !== -1 && targetIndex !== -1) {
        const updatedItems = [...pickerItems];
        updatedItems.splice(currentIndex, 1);
        updatedItems.splice(targetIndex, 0, draggingItem.current);
        setPickerItems(updatedItems);
        onChange(updatedItems.filter((item) => item.isChecked));
      }
    },
    [pickerItems]
  );

  return { handleDragStart, handleDragEnd, handleDragOver, handleDrop };
};

export default useDragDrop;
